import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

//Components
import DataTable from '../../components/DataTable';
import { ActionButtonsForInterview, ResumeButton } from '../../components/ActionButtons';
import { Badge } from 'primereact/badge';

// Full calender component and plugins
import { Calendar, momentLocalizer } from 'react-big-calendar';

//Constants
import { INTERVIEW_COLUMNS } from '../../constants/tableColumns';
import {
  ROUTES,
  INTERVIEW_TYPE,
  DEBOUNCE_TIMEOUT,
  ACTION_TYPE,
  TABLE_NAME,
  HR_OVERALL_FEEDBACK,
  STATUS_INTERVIEW,
  Calendar_Messages,
  INTERVIEW_COLORS,
  eventStyleGetter,
  TooltipContent,
  STATUS,
  INTERVIEW_MODE,
  INTERVIEW_MODE_LIST,
  INTERVIEW_STATUS,
  CANDIDATE_SOURCE_TYPE,
  CANDIDATE_SOURCE_REFERENCE_CODE,
  INTERVIEW_BACKGROUND_COLORS,
  INTERVIEW_BACKGROUND_COLORS_DARK_MODE,
  ROLES,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  INTERVIEW_FILTER_KEY,
  SCHEDULE_STATUSES,
  INTERVIEW_ROUND_RESULT,
  InterviewType
} from '../../constants/common';

//Redux-Actions
import { getInterviewsList } from '../../redux/actions/interviews';

// Utils
import { debounce } from '../../utils/debounce';

// Redux-Slices
import { getNotification } from '../../redux/slices/notification';
import {
  wordCapitalize,
  downloadPdf,
  renderHrAvatar,
  isDarkMode,
  normalizeObject,
  openResumeLink,
  formatExperience
} from '../../utils/common';
import { DROPDOWN_OPTIONS } from '../../constants/dropdownOptions';
import { interviewTableStatus } from '../../redux/slices/interviews';
import AddEditInterviews from './AddEditInterviews';
import { TabPanel, TabView } from 'primereact/tabview';
import { Overlay, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { filteredHrListApi } from '../../redux/apis/hr';
import UserDetails from '../../components/popup/UserDetails';
import { candidateDetailsAction } from '../../redux/actions/userDetails';
import { getInterviewTechnology } from '../../redux/actions/hr';
import CopyToClipboard from '../../components/CopyToClipboard';

const localizer = momentLocalizer(moment);

const pathBase = `${process.env.REACT_APP_API_ENDPOINT}/public/uploads/resume`;

const interviewsTableStates = {
  searchValue: '',
  pageNumber: INITIAL_PAGE_NUMBER,
  size: INITIAL_SIZE,
  sortBy: 'DateModified',
  orderBy: 'desc',
  technology: [],
  interviewRoundStatus: null,
  result: null,
  filterByHR: [],
  viewMode: INTERVIEW_MODE_LIST.LIST
};

const Interviews = () => {
  const dispatch = useDispatch();
  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);
  const navigate = useNavigate();
  const initialDateValue = dayjs();
  const { interviews, userDetails } = useSelector((state) => state);

  const { isNotification } = useSelector((state) => state.notification);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [candidateDetails, setCandidateDetails] = useState([]);
  const [interviewList, setInterviewList] = useState([]);
  const [interviewExport, setInterviewExport] = useState([]);
  const [hrs, setHrs] = useState([]);
  const [editInterviewId, setEditInterviewId] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // const [tableStates, setTableStates] = useState(interviewsTableStates);
  const [showPopup, setShowPopup] = useState(false);
  const [candidateId, setCandidateId] = useState(null);

  const [visibleRight, setVisibleRight] = useState(false);
  const onToggleSiderbar = () => setVisibleRight(!visibleRight);

  const normalizeTableState = (state) => {
    if (!state) return interviewsTableStates;

    return {
      ...interviewsTableStates,
      ...state,
      technology: Array.isArray(state.technology)
        ? state.technology
        : state.technology
        ? [state.technology]
        : []
    };
  };
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem(INTERVIEW_FILTER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const [tableStates, setTableStates] = useState(() => {
    const saved = getSavedFilters();
    return saved ? normalizeTableState(saved) : interviewsTableStates;
  });

  useEffect(() => {
    const { searchValue, technology, interviewRoundStatus, result, filterByHR } = tableStates;

    const isFilterApplied =
      searchValue?.trim() !== '' ||
      technology?.length > 0 ||
      interviewRoundStatus !== null ||
      result !== null ||
      filterByHR?.length > 0;

    if (isFilterApplied) {
      localStorage.setItem(INTERVIEW_FILTER_KEY, JSON.stringify(tableStates));
    } else {
      localStorage.removeItem(INTERVIEW_FILTER_KEY);
    }
  }, [tableStates]);

  useEffect(() => {
    const totalPages = Math.ceil(interviews.pagination.totalRecords / tableStates.size);

    // If current page is no longer valid → reset to page 1
    if (tableStates.pageNumber > totalPages && totalPages > 0) {
      setTableStates((prev) => ({
        ...prev,
        pageNumber: 1
      }));
    }
  }, [interviews.pagination.totalRecords]);

  const onTogglePopup = () => setShowPopup((prevState) => !prevState);

  useEffect(() => {
    filteredHrListApi().then((res) => {
      if (res?.data?.data?.length) {
        const sortedHrs = [...res.data.data].sort((a, b) => {
          if (a.isActive === b.isActive) return 0;
          return a.isActive ? -1 : 1;
        });

        const hrs = sortedHrs.map((hr) => ({
          name: `${hr?.FirstName || ''} ${hr?.LastName || ''}`,
          id: hr?.ID,
          isActive: hr?.isActive
        }));
        setHrs(hrs);
      }
    });
  }, []);

  useEffect(() => {
    dispatch(getInterviewTechnology());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getInterviewsList({
        ...tableStates,
        ...(activeIndex && {
          viewMode: INTERVIEW_MODE_LIST.CALENDER_VIEW,
          pageNumber: 1,
          size: 10000
        })
      })
    );
    if (isNotification) {
      dispatch(getNotification(false));
    }
  }, [
    dispatch,
    activeIndex,
    isNotification,
    tableStates,
    interviews?.isUpdated,
    interviews?.isAdded,
    interviews?.isCancelled
  ]);

  const parseRatings = (ratings) => {
    if (!ratings) return {};
    if (typeof ratings === 'object') return ratings;
    if (typeof ratings === 'string') {
      try {
        return JSON.parse(ratings);
      } catch (e) {
        return {};
      }
    }
    return {};
  };

  const getInterviewDisplayResult = (data) => {
    const isCurrentRoundHr =
      Number(data?.CurrentRound?.InterviewRound) === InterviewType['HR Round'];
    const currentRoundRatings = parseRatings(data?.CurrentRound?.Ratings);
    const roundResult = data?.CurrentRound?.Result?.toString();
    const parentResult = data?.Result?.toString();

    if (isCurrentRoundHr) {
      // For HR round only: if result is selected (1), show rating.result (not result)
      if (roundResult === '1' || parentResult === '1') {
        const ratingResult =
          currentRoundRatings?.Result?.toString() ||
          currentRoundRatings?.OverAllFeedback?.toString() ||
          data?.Ratings?.Result?.toString();
        return ratingResult || '1';
      }
      return roundResult || parentResult || '';
    }

    // For non-HR round: show regular result
    return parentResult || roundResult || '';
  };

  const getAssignedInterviewer = (roundData, interviewStatus) => {
    if (!roundData) return '';

    const isRoundCancelled =
      roundData?.Status === STATUS_INTERVIEW.CANCELLED ||
      interviewStatus === STATUS_INTERVIEW.CANCELLED ||
      Boolean(roundData?.CancellationReason);

    const roundMappings = roundData?.InterviewRoundMapping || [];

    // 1. If active (non-cancelled) interviewers exist in InterviewRoundMapping, show them
    const activeMappings = roundMappings.filter(
      (item) => !item?.CancellationReason && (item?.Examiner?.FirstName || item?.Examiner?.LastName)
    );
    if (activeMappings.length > 0) {
      const activeNames = activeMappings
        .map((item) => {
          const first = item?.Examiner?.FirstName ? wordCapitalize(item?.Examiner?.FirstName) : '';
          const last = item?.Examiner?.LastName ? wordCapitalize(item?.Examiner?.LastName) : '';
          return `${first} ${last}`.trim();
        })
        .filter(Boolean);
      return Array.from(new Set(activeNames)).join(', ');
    }

    // 2. If the round/interview is cancelled and interviewers were mapped (even with CancellationReason),
    // show the assigned examiner(s) who were assigned to this round
    if (isRoundCancelled && roundMappings.length > 0) {
      const cancelledAssigned = roundMappings
        .filter((item) => item?.Examiner?.FirstName || item?.Examiner?.LastName)
        .map((item) => {
          const first = item?.Examiner?.FirstName ? wordCapitalize(item?.Examiner?.FirstName) : '';
          const last = item?.Examiner?.LastName ? wordCapitalize(item?.Examiner?.LastName) : '';
          return `${first} ${last}`.trim();
        })
        .filter(Boolean);
      if (cancelledAssigned.length > 0) {
        return Array.from(new Set(cancelledAssigned)).join(', ');
      }
    }

    // 3. Check accepted / rescheduled invitations from InterviewRoundInvitations
    // NEVER include rejected (STATUS.REJECT), auto-rejected (STATUS.AUTO_REJECT), or accepted-by-other
    const invitations = roundData?.InterviewRoundInvitations || [];
    const acceptedInvitations = invitations.filter(
      (inv) =>
        (inv?.Status === STATUS.ACCEPT || inv?.Status === STATUS.RESCHEDULED) &&
        (inv?.Invited?.FirstName || inv?.Invited?.LastName)
    );
    if (acceptedInvitations.length > 0) {
      const acceptedNames = acceptedInvitations
        .map((inv) => {
          const first = inv?.Invited?.FirstName ? wordCapitalize(inv?.Invited?.FirstName) : '';
          const last = inv?.Invited?.LastName ? wordCapitalize(inv?.Invited?.LastName) : '';
          return `${first} ${last}`.trim();
        })
        .filter(Boolean);
      return Array.from(new Set(acceptedNames)).join(', ');
    }

    // 4. If no assigned/accepted interviewer and round is pending, show valid pending invitations
    // (Still exclude any rejected or auto-rejected invitations)
    const validInvitations = invitations.filter(
      (inv) => inv?.Status !== STATUS.REJECT && (inv?.Invited?.FirstName || inv?.Invited?.LastName)
    );

    if (validInvitations.length > 0) {
      const validNames = validInvitations
        .map((inv) => {
          const first = inv?.Invited?.FirstName ? wordCapitalize(inv?.Invited?.FirstName) : '';
          const last = inv?.Invited?.LastName ? wordCapitalize(inv?.Invited?.LastName) : '';
          return `${first} ${last}`.trim();
        })
        .filter(Boolean);
      return Array.from(new Set(validNames)).join(', ');
    }

    // 5. Fallback for HR round if no examiner was found
    if (roundData?.HROfRound?.FirstName || roundData?.HROfRound?.LastName) {
      const first = roundData?.HROfRound?.FirstName
        ? wordCapitalize(roundData?.HROfRound?.FirstName)
        : '';
      const last = roundData?.HROfRound?.LastName
        ? wordCapitalize(roundData?.HROfRound?.LastName)
        : '';
      return `${first} ${last}`.trim();
    }

    return '';
  };

  useEffect(() => {
    const modifiedCandidate = interviews?.data?.map((interviewData, index) => {
      const cancelledInterviewers = interviewData?.CurrentRound?.InterviewRoundMapping?.filter(
        (item) => item?.CancellationReason
      );
      return {
        ...interviewData,
        CandidateName: (
          <span
            className="table-view-popup-link"
            onClick={() => handleView(interviewData?.Candidate?.ID)}>
            {`${wordCapitalize(interviewData?.Candidate?.FirstName)} ${wordCapitalize(
              interviewData?.Candidate?.LastName
            )}`}
          </span>
        ),
        Company: interviewData?.Candidate?.CurrentCompany
          ? wordCapitalize(interviewData?.Candidate?.CurrentCompany)
          : '--',

        Technology: (
          <ul className="technology-list" key={index}>
            {interviewData?.Candidate?.UMap && interviewData?.Candidate?.UMap?.length > 0
              ? interviewData?.Candidate?.UMap?.map((tech) => (
                  <li
                    key={tech.MapT.ID}
                    style={{
                      backgroundColor: `${
                        isDarkMode() ? tech?.MapT?.DarkModeColor : tech?.MapT?.TechnologyColor
                      }`
                    }}
                    className="technology-badge">
                    {tech.MapT.Technology}
                  </li>
                ))
              : []}
          </ul>
        ),

        Result:
          INTERVIEW_ROUND_RESULT[getInterviewDisplayResult(interviewData)] ||
          `${HR_OVERALL_FEEDBACK.filter(
            (num) => num.code === getInterviewDisplayResult(interviewData)
          ).map((type) => type.name)}` ||
          '--',

        Resume: (
          <div className="flex">
            {interviewData?.Candidate?.ResumeOfCandidate && (
              <>
                <ResumeButton
                  onClick={() => {
                    openResumeLink(
                      `${pathBase}/${interviewData?.Candidate?.ID}/${interviewData?.Candidate?.ResumeOfCandidate?.FilePath}`
                    );
                  }}
                />
                <i
                  data-tip="Download"
                  onClick={() =>
                    downloadPdf(
                      pathBase,
                      interviewData?.Candidate?.ID,
                      interviewData?.Candidate?.ResumeOfCandidate?.FilePath
                    )
                  }
                  className="pi pi-fw pi-download"
                />
              </>
            )}
          </div>
        ),
        HR: renderHrAvatar(
          interviewData?.CurrentRound?.HROfRound?.ID,
          interviewData?.CurrentRound?.HROfRound?.UserProfile,
          interviewData?.CurrentRound?.HROfRound?.FirstName,
          interviewData?.CurrentRound?.HROfRound?.LastName
        ),
        Interviewer: getAssignedInterviewer(interviewData?.CurrentRound, interviewData?.Status),

        JobDesignation:
          interviewData?.JobDesignation &&
          wordCapitalize(interviewData?.JobDesignation?.Designation),
        InterviewRound: `${INTERVIEW_TYPE.filter(
          (num) => num.code === interviewData?.CurrentRound?.InterviewRound
        ).map((type) => type.name)}`,

        Status: (
          <div className="center interview-status">
            {getBadgeAccordingToColor(interviewData)}
            {interviewData?.CurrentRound?.Status === STATUS_INTERVIEW.CANCELLED &&
              (interviewData?.CurrentRound?.CancellationReason ||
                interviewData?.CancellationReason) && (
                <>
                  <i
                    className="custom-target-icon pi pi-info-circle pl-1"
                    data-tip
                    data-for={`reason-tooltip-${index}`}
                    key={index}
                    style={{ cursor: 'pointer' }}
                  />
                  <ReactTooltip key={index} id={`reason-tooltip-${index}`} aria-haspopup="true">
                    {(interviewData?.CurrentRound?.CancelledByUser?.FirstName ||
                      interviewData?.CurrentRound?.CancelledByUser?.LastName) && (
                      <p>{`Cancelled By: ${
                        wordCapitalize(
                          interviewData?.CurrentRound?.CancelledByUser?.FirstName || ''
                        ) +
                        ' ' +
                        wordCapitalize(interviewData?.CurrentRound?.CancelledByUser?.LastName || '')
                      } ${
                        interviewData?.CurrentRound?.CancelledByUser?.UserTypeId === ROLES.HR
                          ? '(HR)'
                          : interviewData?.CurrentRound?.CancelledByUser?.UserTypeId ===
                            ROLES.INTERVIEWER
                          ? '(Interviewer)'
                          : interviewData?.CurrentRound?.CancelledByUser?.UserTypeId ===
                            ROLES.HR_AND_INTERVIEWER
                          ? '(HR and Interviewer)'
                          : ''
                      }`}</p>
                    )}
                    <div
                      style={{
                        maxWidth: '260px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        textAlign: 'left',
                        lineHeight: '1.4'
                      }}>
                      <strong>Reason :</strong>{' '}
                      {interviewData?.CurrentRound?.CancellationReason ||
                        interviewData?.CancellationReason ||
                        '--'}
                    </div>
                  </ReactTooltip>
                </>
              )}
            {SCHEDULE_STATUSES.includes(interviewData?.CurrentRound?.Status) &&
              cancelledInterviewers?.length > 0 && (
                <>
                  <i
                    className="custom-target-icon pi pi-info-circle pl-1"
                    data-tip
                    data-for={`schedule-reason-tooltip-${index}`}
                    style={{ cursor: 'pointer' }}
                  />

                  <ReactTooltip id={`schedule-reason-tooltip-${index}`}>
                    {cancelledInterviewers.map((item, i) => (
                      <div key={i}>
                        <div style={{ marginBottom: '4px' }}>
                          Cancelled By: {wordCapitalize(item?.Examiner?.FirstName)}{' '}
                          {wordCapitalize(item?.Examiner?.LastName)}
                        </div>

                        <div
                          style={{
                            maxWidth: '260px',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            marginBottom: '8px'
                          }}>
                          <strong>Reason :</strong> {item?.CancellationReason}
                        </div>
                      </div>
                    ))}
                  </ReactTooltip>
                </>
              )}
          </div>
        ),

        action: (
          <div className="flex align-items-center">
            <ActionButtonsForInterview
              disabledSendJD={interviewData?.CurrentRound?.Status === STATUS_INTERVIEW.CANCELLED}
              onClick={(actionType) => handleRowActions(actionType, interviewData.ID)}
            />
          </div>
        )
      };
    });
    setInterviewList(modifiedCandidate);
    const interviewsOnCalender = [];
    interviews?.data?.length > 0 &&
      interviews?.data.forEach((interview) => {
        const technology =
          interview?.Candidate?.UMap?.length > 0
            ? interview?.Candidate?.UMap?.map((item) => item.MapT.Technology).join(', ')
            : '';
        !!interview?.RoundOfInterview?.length &&
          interview.RoundOfInterview.forEach((round) => {
            const getDisplayEndTime = (start, end) => {
              const startTime = moment(start);
              const endTime = moment(end);
              const diffInMinutes = endTime.diff(startTime, 'minutes');
              const displayMinutes = diffInMinutes <= 30 ? 30 : 60;
              return startTime.clone().add(displayMinutes, 'minutes');
            };

            const startTime = moment(round.StartDateOfInterviewRound);
            const displayEndTime = getDisplayEndTime(
              round.StartDateOfInterviewRound,
              round.EndDateOfInterviewRound
            );

            interviewsOnCalender.push({
              interviewId: interview.ID,
              title: `${startTime.format('HH:mm')} - ${displayEndTime.format('HH:mm')} ${
                interview?.Candidate?.FirstName || ''
              } ${interview?.Candidate?.LastName || ''}`,
              start: round.StartDateOfInterviewRound,
              end: round.EndDateOfInterviewRound,
              technology: technology,
              interviewer: getAssignedInterviewer(round, interview?.Status),
              interviewLevel: INTERVIEW_TYPE.find((num) => num.code === round?.InterviewRound)
                ?.name,
              interviewMode: INTERVIEW_MODE.find((num) => num.code === round.InterviewRoundMode)
                ?.name,
              scheduleBy:
                (round?.HROfRound?.FirstName || '') + ' ' + (round?.HROfRound?.LastName || ''),
              jobTitle:
                interview?.Candidate?.UMap?.length > 0 &&
                interview?.Candidate?.UMap.map((obj) => obj.MapT.Technology).join(', '),
              color:
                round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_PENDING ||
                round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_PENDING ||
                round?.Status === STATUS_INTERVIEW.MANAGEMENT_PENDING ||
                round.Status === STATUS_INTERVIEW.PRACTICAL_PENDING ||
                round.Status === STATUS_INTERVIEW.HR_PENDING
                  ? INTERVIEW_COLORS.PENDING
                  : round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_SCHEDULE ||
                    round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_SCHEDULE ||
                    round?.Status === STATUS_INTERVIEW.MANAGEMENT_SCHEDULE ||
                    round.Status === STATUS_INTERVIEW.PRACTICAL_SCHEDULE ||
                    round.Status === STATUS_INTERVIEW.HR_SCHEDULE
                  ? INTERVIEW_COLORS.SCHEDULE
                  : round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_COMPLETE ||
                    round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_COMPLETE ||
                    round?.Status === STATUS_INTERVIEW.MANAGEMENT_COMPLETE ||
                    round.Status === STATUS_INTERVIEW.PRACTICAL_COMPLETE ||
                    round.Status === STATUS_INTERVIEW.HR_COMPLETE
                  ? INTERVIEW_COLORS.COMPLETE
                  : round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_RESCHEDULE ||
                    round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_RESCHEDULE ||
                    round?.Status === STATUS_INTERVIEW.MANAGEMENT_RESCHEDULE ||
                    round.Status === STATUS_INTERVIEW.PRACTICAL_RESCHEDULE ||
                    round.Status === STATUS_INTERVIEW.HR_RESCHEDULE
                  ? INTERVIEW_COLORS.RESCHEDULE
                  : round.Status === STATUS_INTERVIEW.CANCELLED
                  ? INTERVIEW_COLORS.CANCEL
                  : '',
              backgroundColor:
                round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_PENDING ||
                round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_PENDING ||
                round?.Status === STATUS_INTERVIEW.MANAGEMENT_PENDING ||
                round.Status === STATUS_INTERVIEW.PRACTICAL_PENDING ||
                round.Status === STATUS_INTERVIEW.HR_PENDING
                  ? isDarkMode()
                    ? INTERVIEW_BACKGROUND_COLORS_DARK_MODE.PENDING
                    : INTERVIEW_BACKGROUND_COLORS.PENDING
                  : round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_SCHEDULE ||
                    round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_SCHEDULE ||
                    round?.Status === STATUS_INTERVIEW.MANAGEMENT_SCHEDULE ||
                    round.Status === STATUS_INTERVIEW.PRACTICAL_SCHEDULE ||
                    round.Status === STATUS_INTERVIEW.HR_SCHEDULE
                  ? isDarkMode()
                    ? INTERVIEW_BACKGROUND_COLORS_DARK_MODE.SCHEDULE
                    : INTERVIEW_BACKGROUND_COLORS.SCHEDULE
                  : round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_COMPLETE ||
                    round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_COMPLETE ||
                    round?.Status === STATUS_INTERVIEW.MANAGEMENT_COMPLETE ||
                    round.Status === STATUS_INTERVIEW.PRACTICAL_COMPLETE ||
                    round.Status === STATUS_INTERVIEW.HR_COMPLETE
                  ? isDarkMode()
                    ? INTERVIEW_BACKGROUND_COLORS_DARK_MODE.COMPLETE
                    : INTERVIEW_BACKGROUND_COLORS.COMPLETE
                  : round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_RESCHEDULE ||
                    round?.Status === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_RESCHEDULE ||
                    round?.Status === STATUS_INTERVIEW.MANAGEMENT_RESCHEDULE ||
                    round.Status === STATUS_INTERVIEW.PRACTICAL_RESCHEDULE ||
                    round.Status === STATUS_INTERVIEW.HR_RESCHEDULE
                  ? isDarkMode()
                    ? INTERVIEW_BACKGROUND_COLORS_DARK_MODE.RESCHEDULE
                    : INTERVIEW_BACKGROUND_COLORS.RESCHEDULE
                  : round.Status === STATUS_INTERVIEW.CANCELLED
                  ? isDarkMode()
                    ? INTERVIEW_BACKGROUND_COLORS_DARK_MODE.CANCEL
                    : INTERVIEW_BACKGROUND_COLORS.CANCEL
                  : ''
            });
          });
      });
    setSelectedEvents(interviewsOnCalender);
  }, [interviews, isDarkMode()]);

  useEffect(() => {
    candidateId && dispatch(candidateDetailsAction(candidateId));
  }, [dispatch, candidateId]);

  const handleView = (ID) => {
    if (ID) {
      onTogglePopup();
      setCandidateId(ID);
    }
  };

  const modifyUserDetails = useCallback(() => {
    if (userDetails?.ID) {
      // Display -- if the candidate mobile number is not provided.
      let mobileValue = '--';
      if (userDetails?.Mobile) {
        mobileValue = userDetails?.Mobile;
      }
      if (mobileValue && mobileValue === 'null') {
        mobileValue = '--';
      }
      const candidateDetailsData = [
        {
          title: 'Name',
          value: `${wordCapitalize(userDetails?.FirstName) || ''} 
              ${wordCapitalize(userDetails?.LastName) || ''}`
        },
        {
          title: 'Gender',
          value: userDetails?.Gender == 1 ? 'Male' : userDetails?.Gender == 2 ? 'Female' : '--'
        },
        {
          title: 'Experience',
          value: formatExperience(userDetails?.YearsOfExperience, userDetails?.MonthsOfExperience)
        },
        {
          title: 'Email Address',
          value: (
            <div className="center">
              <span>{userDetails?.Email || '--'}</span>
              {userDetails?.Email && <CopyToClipboard text={userDetails?.Email} />}
            </div>
          )
        },
        {
          title: 'Mobile',
          value: (
            <div className="center">
              <span>{mobileValue || '--'}</span>
              {userDetails?.Mobile && <CopyToClipboard text={mobileValue} />}
            </div>
          )
        },
        {
          title: 'Second Mobile',
          value: (
            <div className="center">
              <span>{userDetails?.SecondaryMobile || '--'}</span>
              {userDetails?.SecondaryMobile && (
                <CopyToClipboard text={userDetails?.SecondaryMobile} />
              )}
            </div>
          )
        },
        {
          title: 'Current Company',
          value: userDetails?.CurrentCompany || ''
        },
        {
          title: 'Technology',
          value:
            userDetails.CandidateTechnology && userDetails.CandidateTechnology.length > 0
              ? userDetails.CandidateTechnology?.map((tech) => tech.Technology).join(', ')
              : ''
        },
        {
          title: 'Current CTC',
          value: userDetails?.CurrentCTC ? `${userDetails?.CurrentCTC} LPA` : ''
        },
        {
          title: 'Candidate Location',
          value: userDetails?.Address || ''
        },
        {
          title: 'Candidate Source',
          value: CANDIDATE_SOURCE_TYPE.find((type) => type.code === userDetails?.CandidateSource)
            ?.name
        }
      ];
      // Only add the candidate reference name if candidate provided source is reference.
      if (userDetails?.CandidateSource === CANDIDATE_SOURCE_REFERENCE_CODE) {
        candidateDetailsData.push({
          title: 'Employee Ref Name',
          value: userDetails?.EmployeeReferenceName || ''
        });
      }
      candidateDetailsData.push(
        {
          title: 'Created By',
          value: renderHrAvatar(
            userDetails?.ReportsToUser?.ID,
            userDetails?.ReportsToUser?.UserProfile,
            userDetails?.ReportsToUser?.FirstName,
            userDetails?.ReportsToUser?.LastName,
            true
          )
        },
        {
          title: 'Last Updated Time',
          value: userDetails?.DateModified ? moment(userDetails?.DateModified).format('lll') : null
        }
      );
      setCandidateDetails(candidateDetailsData);
    }
  }, [userDetails]);

  useEffect(() => {
    modifyUserDetails();
  }, [userDetails, modifyUserDetails]);

  useEffect(() => {
    const modifiedCandidate = interviews?.data?.map((interviewData) => {
      const Type =
        interviewData?.Candidate?.UMap?.length > 0
          ? interviewData?.Candidate?.UMap?.map((item) => item.MapT.Technology).join(', ')
          : '';
      return {
        CandidateName: `${wordCapitalize(interviewData?.Candidate?.FirstName)} ${wordCapitalize(
          interviewData?.Candidate?.LastName
        )}`,
        Technology: Type,
        Result: `${HR_OVERALL_FEEDBACK.filter((num) => num.code === interviewData.Result).map(
          (type) => type.name
        )}`,
        JobDesignation:
          interviewData?.JobDesignation &&
          wordCapitalize(interviewData?.JobDesignation?.Designation)
      };
    });
    setInterviewExport(modifiedCandidate);
  }, [interviews]);

  const handlePagination = (pageNumber, size) =>
    setTableStates((prevState) => {
      return {
        ...prevState,
        pageNumber,
        size
      };
    });

  const handleSearch = (searchValue) =>
    setTableStates((prevState) => {
      return {
        ...prevState,
        searchValue
      };
    });

  const handleSort = (sortBy) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  const handleStatusFilter = (interviewRoundStatus) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        interviewRoundStatus: interviewRoundStatus || null
      };
    });
  };

  const handleRowActions = (actionType, rowId) => {
    if (actionType === ACTION_TYPE.NEXT_ROUND) {
      dispatch(interviewTableStatus(tableStates.interviewRoundStatus));
      navigate(`${ROUTES.HR.INTERVIEW_ROUND}/${rowId}`);
    }
  };

  const handleAdd = () => {
    setEditInterviewId(null);
    onToggleSiderbar();
    setVisibleRight(true);
    document.body.classList.add('sidebar-open');
  };

  const getBadgeAccordingToColor = (interviewData) => {
    const badgeArr = [];
    INTERVIEW_STATUS.forEach((statusObj) => {
      if (statusObj.code === Number(interviewData?.CurrentRound?.Status)) {
        let badgeColor;
        switch (interviewData?.CurrentRound?.Status) {
          case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_SCHEDULE:
          case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_SCHEDULE:
          case STATUS_INTERVIEW.MANAGEMENT_SCHEDULE:
          case STATUS_INTERVIEW.PRACTICAL_SCHEDULE:
          case STATUS_INTERVIEW.HR_SCHEDULE: {
            badgeColor = 'schedule-badge';
            break;
          }
          case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_RESCHEDULE:
          case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_RESCHEDULE:
          case STATUS_INTERVIEW.MANAGEMENT_RESCHEDULE:
          case STATUS_INTERVIEW.HR_RESCHEDULE:
          case STATUS_INTERVIEW.PRACTICAL_RESCHEDULE: {
            badgeColor = 'reschedule-badge';
            break;
          }
          case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_COMPLETE:
          case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_COMPLETE:
          case STATUS_INTERVIEW.MANAGEMENT_COMPLETE:
          case STATUS_INTERVIEW.PRACTICAL_COMPLETE:
          case STATUS_INTERVIEW.HR_COMPLETE: {
            badgeColor = 'complete-badge';
            break;
          }
          case STATUS_INTERVIEW.CANCELLED: {
            badgeColor = 'cancel-badge';
            break;
          }
          default: {
            badgeColor = 'pending-badge';
            break;
          }
        }
        badgeArr.push(
          <Badge
            key={interviewData?.CurrentRound?.Status}
            className={`white-space-nowrap ${badgeColor}`}
            value={
              [
                STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_RESCHEDULE,
                STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_RESCHEDULE,
                STATUS_INTERVIEW.PRACTICAL_RESCHEDULE,
                STATUS_INTERVIEW.MANAGEMENT_RESCHEDULE,
                STATUS_INTERVIEW.HR_RESCHEDULE
              ].indexOf(interviewData?.CurrentRound?.Status) !== -1
                ? `${statusObj.name} -  ${
                    interviewData?.CurrentRound?.InterviewRoundInvitations.filter(
                      (child) => child.Status === STATUS.ACCEPT
                    ).length >= 1
                      ? 'Accepted'
                      : 'Pending'
                  }`
                : `${statusObj.name}`
            }
          />
        );
      }
    });
    return badgeArr;
  };

  const CustomEvent = ({ event }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const closeTooltip = () => {
      setShowTooltip(false);
    };

    const openTooltip = () => {
      setShowTooltip(true);
    };

    const ref = useRef(null);

    const getTarget = () => {
      return ref.current;
    };

    const handleRef = (element) => {
      if (element) {
        ref.current = element.parentNode;
        ref.current.addEventListener('mouseover', openTooltip);
        ref.current.addEventListener('mouseout', closeTooltip);
      }
    };

    return (
      <div ref={handleRef}>
        <span style={{ whiteSpace: 'break-spaces' }}>{event.title}</span>
        <Overlay
          rootClose
          target={getTarget}
          show={showTooltip}
          placement="bottom"
          onHide={closeTooltip}>
          <Tooltip id="test" className="calender-tooltip">
            <TooltipContent event={event} />
          </Tooltip>
        </Overlay>
      </div>
    );
  };

  const handleFilterByHr = (filterByHR) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        filterByHR
      };
    });
  };

  const handleTechnologyFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        technology: value || []
      };
    });
  };

  const handleResultFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        result: value || null
      };
    });
  };
  const [searchParams] = useSearchParams();
  const [initialCalendarView, setInitialCalendarView] = useState('month');

  useEffect(() => {
    const tab = searchParams.get('tab');
    const view = searchParams.get('view');

    if (tab === 'calendar') {
      setActiveIndex(1); // open Calendar tab
      if (view === 'day') setInitialCalendarView('day');
      else if (view === 'week') setInitialCalendarView('week');
      else setInitialCalendarView('month');
    }
  }, [searchParams]);

  return (
    <div className="card interview-card">
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => setActiveIndex(e.index)}
        className="schedule-tabbing-wrap custom-tabbing">
        <TabPanel header="List view">
          {visibleRight && (
            <AddEditInterviews ID={editInterviewId} onHide={onToggleSiderbar} show={visibleRight} />
          )}
          <DataTable
            onAdd={() => handleAdd()}
            searchPlaceHolder="Search by Candidate name, Email and Mobile"
            searchValue={tableStates.searchValue}
            name={TABLE_NAME.INTERVIEW}
            totalRecords={interviews.pagination.totalRecords}
            pageNumber={tableStates.pageNumber}
            pageSize={tableStates.size}
            onPagination={handlePagination}
            onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
            onSort={handleSort}
            data={interviewList}
            hrFilter
            hrs={hrs}
            onFilter={handleFilterByHr}
            selectedHrs={tableStates.filterByHR}
            exportData={interviewExport}
            columns={INTERVIEW_COLUMNS}
            isDropdownMultiSelect
            dropdownPlaceHolder="Select Technology"
            onStatusFilter={handleTechnologyFilter}
            dropdownValue={tableStates.technology}
            dropdown={
              technologyListData?.length
                ? technologyListData.map((tech) => ({ name: tech.Technology, value: tech.ID }))
                : []
            }
            secondDropdownValue={tableStates.interviewRoundStatus}
            secondDropdown={DROPDOWN_OPTIONS}
            secondDropdownPlaceHolder="Select Status"
            onSecondDropdownChange={handleStatusFilter}
            thirdDropdownPlaceHolder="Select Result"
            onThirdDropdownChange={handleResultFilter}
            thirdDropdownValue={tableStates.result}
            thirdDropdown={HR_OVERALL_FEEDBACK.map((feedback) => ({
              name: feedback.name,
              value: feedback.code
            }))}
            clearFilter
            isClearFilterDisabled={
              JSON.stringify(normalizeObject(interviewsTableStates)) ===
              JSON.stringify(normalizeObject(tableStates))
            }
            clearFilterOnClick={() => {
              setTableStates({
                ...interviewsTableStates,
                pageNumber: INITIAL_PAGE_NUMBER,
                size: INITIAL_SIZE
              });
              localStorage.removeItem(INTERVIEW_FILTER_KEY);
            }}
          />
        </TabPanel>
        <TabPanel header="Calender view">
          <div className="interview-calender-view">
            <div className="col-12 p-0 full-calender-wrapper">
              {initialDateValue && (
                <div className={`calendar-wrap ${isDarkMode() && 'dark-mode'}`}>
                  <Calendar
                    key={initialCalendarView}
                    defaultView={initialCalendarView}
                    localizer={localizer}
                    events={selectedEvents}
                    startAccessor={(e) => new Date(e.start)}
                    onSelectEvent={(event) => navigate(`/hr/interviews/${event.interviewId}`)}
                    eventPropGetter={eventStyleGetter}
                    tooltipAccessor={null}
                    messages={Calendar_Messages}
                    views={['month', 'week', 'day']}
                    showAllEvents
                    components={{
                      event: CustomEvent
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </TabPanel>
      </TabView>
      <div className="legends">
        <div className="legend-label">
          <span className="pending-label" />
          Pending
        </div>
        <div className="legend-label">
          <span className="schedule-label" />
          Schedule
        </div>
        <div className="legend-label">
          <span className="reschedule-label" />
          Reschedule
        </div>
        <div className="legend-label">
          <span className="complete-label" />
          Completed
        </div>
        <div className="legend-label">
          <span className="cancel-label" />
          Cancel
        </div>
      </div>

      <UserDetails
        header="Candidate Details"
        data={candidateDetails}
        onHide={onTogglePopup}
        show={showPopup}
      />
    </div>
  );
};

export default Interviews;
